import React, { createContext, useContext, useState, useEffect, useMemo, useCallback } from 'react';
import api from '../utils/api';
import { RESERVED_PATHS, getTenantSlugFromPath as getTenantSlugFromPathStatic } from '../utils/tenant';

/**
 * TenantContext - Manages multi-tenant state
 * 
 * The tenant slug is extracted from the URL path (e.g., /cherry-holidays/dashboard)
 * and used to:
 * 1. Fetch tenant branding/settings
 * 2. Add X-Tenant-Slug header to all API requests
 * 3. Scope all data operations to the current tenant
 */

const TenantContext = createContext(null);

export const useTenant = () => {
  const context = useContext(TenantContext);
  if (!context) {
    throw new Error('useTenant must be used within a TenantProvider');
  }
  return context;
};

export const TenantProvider = ({ children }) => {
  // Track pathname changes for reactive tenant slug
  const [pathname, setPathname] = useState(window.location.pathname);
  
  // Listen to navigation events to update pathname
  useEffect(() => {
    const handleLocationChange = () => {
      setPathname(window.location.pathname);
    };
    
    // Listen to popstate (back/forward buttons)
    window.addEventListener('popstate', handleLocationChange);
    
    // Override pushState and replaceState to detect programmatic navigation
    const originalPushState = window.history.pushState;
    const originalReplaceState = window.history.replaceState;
    
    window.history.pushState = function(...args) {
      originalPushState.apply(this, args);
      handleLocationChange();
    };
    
    window.history.replaceState = function(...args) {
      originalReplaceState.apply(this, args);
      handleLocationChange();
    };
    
    return () => {
      window.removeEventListener('popstate', handleLocationChange);
      window.history.pushState = originalPushState;
      window.history.replaceState = originalReplaceState;
    };
  }, []);
  
  // Try to get cached tenant from sessionStorage for instant render
  const getCachedTenant = useCallback(() => {
    try {
      const cached = sessionStorage.getItem('tenant_cache');
      if (cached) {
        const { data, slug, timestamp } = JSON.parse(cached);
        // Use cache if less than 5 minutes old and same tenant
        const tenantSlug = getTenantSlugFromPathStatic();
        if (slug === tenantSlug && Date.now() - timestamp < 5 * 60 * 1000) {
          return data;
        }
      }
    } catch (e) {}
    return null;
  }, []);

  const [tenant, setTenant] = useState(() => getCachedTenant());
  const [loading, setLoading] = useState(!getCachedTenant());
  const [error, setError] = useState(null);

  // Extract tenant slug from URL path - now reactive to pathname changes
  const tenantSlug = useMemo(() => {
    const pathParts = pathname.split('/').filter(Boolean);
    // First part of path should be the tenant slug
    // Unless it's a root-level route like /admin, /register
    if (pathParts.length > 0 && !RESERVED_PATHS.includes(pathParts[0])) {
      return pathParts[0];
    }
    return null;
  }, [pathname]);

  useEffect(() => {
    const fetchTenant = async () => {
      if (!tenantSlug) {
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        setError(null);
        
        const response = await api.get(`/tenants/by-slug/${tenantSlug}`);
        setTenant(response.data);
        
        // Cache tenant data in sessionStorage for instant render on page reload
        try {
          sessionStorage.setItem('tenant_cache', JSON.stringify({
            data: response.data,
            slug: tenantSlug,
            timestamp: Date.now()
          }));
        } catch (e) {}
        
        // Store tenant slug in localStorage for persistence
        localStorage.setItem('tenant_slug', tenantSlug);
        
        // Update document title with tenant name
        if (response.data.company_name) {
          document.title = `${response.data.company_name} - Quotation Builder`;
        }
        
        // Update favicon if tenant has one
        if (response.data.favicon_url) {
          const link = document.querySelector("link[rel~='icon']") || document.createElement('link');
          link.rel = 'icon';
          link.href = response.data.favicon_url;
          document.head.appendChild(link);
        }
      } catch (err) {
        console.error('Failed to fetch tenant:', err);
        setError(err.response?.data?.detail || 'Tenant not found');
        setTenant(null);
      } finally {
        setLoading(false);
      }
    };

    fetchTenant();
  }, [tenantSlug]);

  // Helper to build tenant-scoped URLs
  const buildPath = (path) => {
    // Ensure path starts with /
    const cleanPath = path.startsWith('/') ? path : `/${path}`;
    
    // If we have a tenant slug from URL, use it
    if (tenantSlug) {
      return `/${tenantSlug}${cleanPath}`;
    }
    
    // Try to get tenant from stored user data
    const savedUser = localStorage.getItem('user');
    if (savedUser) {
      try {
        const userData = JSON.parse(savedUser);
        if (userData.tenant_slug) {
          return `/${userData.tenant_slug}${cleanPath}`;
        }
      } catch (e) {
        // Ignore parse errors
      }
    }
    
    // Fall back to default tenant
    return `/cherry-holidays${cleanPath}`;
  };

  // Navigate within tenant context
  const navigateTo = (path) => {
    window.location.href = buildPath(path);
  };

  const value = {
    tenant,
    tenantSlug,
    loading,
    error,
    buildPath,
    navigateTo,
    isLoaded: !loading && !error,
  };

  return (
    <TenantContext.Provider value={value}>
      {children}
    </TenantContext.Provider>
  );
};

export default TenantContext;
